import React, { useState, useEffect } from 'react';
import { expensesAPI } from '../services/api';
import ActionModal from '../components/ActionModal';
import Toast from '../components/Toast';
import './ExpensesPage.css';

// Eliminamos las categorías visuales, todos los gastos irán por defecto a 'otros'
const CATEGORY_COLORS = {
    otros: { background: '#f5f5f5', color: '#424242' },
};

// ── Formulario interno ──
const ExpenseForm = ({ initialData, onSubmit, onClose }) => {
    const [form, setForm] = useState({
        description: initialData?.description || '',
        amount: initialData?.amount || '',
        category: initialData?.category || 'otros',
        expense_date: initialData?.expense_date || new Date().toISOString().split('T')[0],
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.description || !form.amount) return;
        setIsSaving(true);
        await onSubmit(form);
        setIsSaving(false);
    };

    return (
        <form onSubmit={handleSubmit} className="expense-form">
            <div className="form-group">
                <label>Concepto de Gasto *</label>
                <input
                    type="text"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Ej: Combustible para transporte, Mantenimiento, Limpieza..."
                    required
                    className="form-input"
                />
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Monto en dólares ($ USD) *</label>
                    <input
                        type="number"
                        name="amount"
                        value={form.amount}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0.01"
                        step="0.01"
                        required
                        className="form-input"
                    />
                </div>
                <div className="form-group">
                    <label>Fecha de Consumo *</label>
                    <input
                        type="date"
                        name="expense_date"
                        value={form.expense_date}
                        onChange={handleChange}
                        required
                        className="form-input"
                    />
                </div>
            </div>

            {/* Se elimina el selector de Categoría ya que ahora todo es campo libre */}

            <div className="form-actions">
                <button type="button" onClick={onClose} className="btn-cancel">
                    Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn-save">
                    {isSaving ? '⏳ Guardando...' : '💾 Guardar Gasto'}
                </button>
            </div>
        </form>
    );
};

// ── Página principal ──
const ExpensesPage = () => {
    // ==================== ESTADOS ====================
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalExpenses, setTotalExpenses] = useState(0);

    // Filtros
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    // Modal Formulario
    const [showFormModal, setShowFormModal] = useState(false);

    // ActionModal
    const [actionExpense, setActionExpense] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);

    // Toast
    const [toast, setToast] = useState(null);

    // Detectar móvil (igual que OrdersManagementPage)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // ==================== EFECTOS ====================
    useEffect(() => {
        loadExpenses();
    }, [filterCategory, filterStartDate, filterEndDate]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const showToast = (message, type = 'success') => setToast({ message, type });

    // ==================== FUNCIONES ====================
    const loadExpenses = async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (filterCategory) params.category = filterCategory;
            if (filterStartDate) params.startDate = filterStartDate;
            if (filterEndDate) params.endDate = filterEndDate;

            const response = await expensesAPI.getAll(params);
            setExpenses(response.data.data || []);
            setTotalExpenses(response.data.total || 0);
        } catch (error) {
            console.error(error);
            showToast('Error al cargar los gastos', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveExpense = async (formData) => {
        try {
            await expensesAPI.create(formData);
            showToast('✅ Gasto registrado correctamente', 'success');
            setShowFormModal(false);
            loadExpenses();
        } catch (error) {
            showToast(error.response?.data?.message || '❌ Error al guardar', 'error');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este gasto?')) return;
        try {
            await expensesAPI.delete(id);
            showToast('🗑️ Gasto eliminado', 'success');
            setShowActionModal(false);
            loadExpenses();
        } catch (error) {
            showToast('❌ Error al eliminar', 'error');
        }
    };

    const handleOpenActionModal = (e, expense) => {
        e.stopPropagation();
        setActionExpense(expense);
        setShowActionModal(true);
    };

    const handleCloseActionModal = () => {
        setShowActionModal(false);
        setTimeout(() => setActionExpense(null), 300);
    };

    const getExpenseActions = () => {
        if (!actionExpense) return [];
        return [
            {
                icon: '🗑️',
                title: 'Eliminar Gasto',
                subtitle: 'Esta acción es irreversible',
                variant: 'delete',
                onClick: () => handleDelete(actionExpense.id),
            },
        ];
    };

    const clearFilters = () => {
        setFilterCategory('');
        setFilterStartDate('');
        setFilterEndDate('');
        setSearchTerm('');
    };

    // Filtro local por texto
    const filteredExpenses = expenses.filter(e =>
        e.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ── Tarjeta móvil para cada gasto ──
    const MobileExpenseCard = ({ expense }) => {
        const dateStr = expense.expense_date
            ? new Date(expense.expense_date).toLocaleDateString('es-PE', {
                day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC'
            })
            : '-';
        return (
            <div className="order-card-mobile">
                <div className="order-card-top">
                    <span className="order-card-customer" style={{ flex: 1 }}>{expense.description}</span>
                    <button className="kebab-btn" onClick={(e) => handleOpenActionModal(e, expense)}>⋮</button>
                </div>
                <div className="order-card-bottom">
                    <span>
                        <span className="status-badge" style={CATEGORY_COLORS.otros}>
                            GASTO
                        </span>
                    </span>
                    <span>📅 {dateStr}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#c62828' }}>
                        ${parseFloat(expense.amount).toFixed(2)} USD
                    </span>
                </div>
            </div>
        );
    };

    // ==================== RENDER ====================
    return (
        <div className="content-pane">

            {/* Header */}
            <div className="orders-header">
                <h2>💸 Gestión de Gastos</h2>
                <p className="orders-header-subtitle">
                    Registra y controla los gastos del negocio (Combustible, mantenimiento y gastos operativos)
                </p>
            </div>

            {/* KPI rápido */}
            <div className="expense-kpi-row">
                <div className="expense-kpi-card">
                    <span className="expense-kpi-label">Total del período</span>
                    <span className="expense-kpi-value">${totalExpenses.toFixed(2)} USD</span>
                </div>
                <div className="expense-kpi-card">
                    <span className="expense-kpi-label">Registros</span>
                    <span className="expense-kpi-value">{filteredExpenses.length}</span>
                </div>
            </div>

            {/* Barra de Acciones */}
            <div className="filters-container" style={{ flexWrap: 'wrap', gap: 10 }}>

                {/* Búsqueda por texto */}
                <div className="search-container" style={{ margin: 0, flex: '1 1 200px' }}>
                    <input
                        type="text"
                        placeholder="🔍 Buscar gasto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                {/* Se eliminó el filtro de categoría */}

                {/* Filtro fecha inicio */}
                <input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    className="search-input"
                    style={{ flex: '0 1 150px' }}
                    title="Desde"
                />

                {/* Filtro fecha fin */}
                <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="search-input"
                    style={{ flex: '0 1 150px' }}
                    title="Hasta"
                />

                {/* Limpiar filtros */}
                {(filterCategory || filterStartDate || filterEndDate || searchTerm) && (
                    <button onClick={clearFilters} className="btn-clear-filters">
                        ✕ Limpiar
                    </button>
                )}

                {/* Nuevo Gasto */}
                <button onClick={() => setShowFormModal(true)} className="create-order-btn">
                    ➕ Nuevo Gasto
                </button>
            </div>

            {/* Tabla (PC) / Tarjetas (Móvil) */}
            {isLoading ? (
                <div className="loading-container"><p>⏳ Cargando gastos...</p></div>
            ) : filteredExpenses.length === 0 ? (
                <div className="empty-container">
                    <p>💸 No hay gastos registrados{filterCategory || filterStartDate ? ' para este filtro' : ''}.</p>
                </div>
            ) : isMobile ? (
                // ── VISTA MÓVIL: Tarjetas ──
                <div className="orders-cards-container">
                    {filteredExpenses.map(expense => (
                        <MobileExpenseCard key={expense.id} expense={expense} />
                    ))}
                    {/* Total al pie */}
                    <div className="order-card-mobile" style={{ borderTop: '2px solid #fee2e2', background: '#fff5f5' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                            <span>Total mostrado</span>
                            <span style={{ color: '#c62828' }}>
                                ${filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)} USD
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                // ── VISTA PC: Tabla ──
                <div className="table-container">
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>Concepto del Gasto</th>
                                <th>Fecha de Consumo</th>
                                <th className="center">Monto (USD)</th>
                                <th className="center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredExpenses.map(expense => (
                                <tr key={expense.id}>
                                    <td>{expense.description}</td>
                                    <td>
                                        {expense.expense_date
                                            ? new Date(expense.expense_date).toLocaleDateString('es-PA', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                                timeZone: 'UTC'
                                            })
                                            : '-'}
                                    </td>
                                    <td className="center">
                                        <strong style={{ color: '#c62828' }}>
                                            ${parseFloat(expense.amount).toFixed(2)} USD
                                        </strong>
                                    </td>
                                    <td className="center">
                                        <button
                                            className="kebab-btn"
                                            onClick={(e) => handleOpenActionModal(e, expense)}
                                        >⋮</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="expense-total-row">
                                <td colSpan={2}><strong>Total mostrado</strong></td>
                                <td className="center">
                                    <strong style={{ color: '#c62828' }}>
                                        ${filteredExpenses
                                            .reduce((sum, e) => sum + parseFloat(e.amount), 0)
                                            .toFixed(2)} USD
                                    </strong>
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Modal Formulario */}
            {showFormModal && (
                <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
                    <div className="modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h2 style={{ margin: 0 }}>💸 Registrar Gasto</h2>
                            <button
                                onClick={() => setShowFormModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
                            >
                                ✕
                            </button>
                        </div>
                        <ExpenseForm
                            onSubmit={handleSaveExpense}
                            onClose={() => setShowFormModal(false)}
                        />
                    </div>
                </div>
            )}

            {/* ActionModal */}
            <ActionModal
                isOpen={showActionModal}
                onClose={handleCloseActionModal}
                title="Acciones del Gasto"
                itemName={actionExpense?.description}
                itemSubtitle={actionExpense ? `S/ ${parseFloat(actionExpense.amount).toFixed(2)}` : ''}
                actions={getExpenseActions()}
            />

            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
            )}
        </div>
    );
};

export default ExpensesPage;